import { ReactNode, useState, useEffect } from 'react';
import { Redirect, useLocation } from 'wouter';
import Cookies from 'js-cookie';
import { useAuth } from '@/contexts/auth-context';
import { organizationService } from '@/services/api/organization.service';
import { User } from '@/types/auth';
import { Spinner } from '@/components/ui/spinner';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: User['role'];
  redirectTo?: string;
}

export function ProtectedRoute({
  children,
  requiredRole,
  redirectTo = '/login'
}: ProtectedRouteProps) {
  const { user, loading, isAuthenticated, impersonation } = useAuth();
  const [location] = useLocation();
  const [checkingOnboarding, setCheckingOnboarding] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);
  const [hydrationTimedOut, setHydrationTimedOut] = useState(false);

  // The hydration wait below is for a one-render gap, not an outage. If the
  // session never arrives — /auth/me failed on the network, so the token is
  // still sitting in the cookie — fall back to the login page rather than
  // spinning forever.
  useEffect(() => {
    if (isAuthenticated) {
      setHydrationTimedOut(false);
      return;
    }

    const timer = setTimeout(() => setHydrationTimedOut(true), 3000);
    return () => clearTimeout(timer);
  }, [isAuthenticated]);

  // Reset onboarding check when user is on the onboarding page
  // This ensures we re-check when they navigate away after completing
  useEffect(() => {
    if (location === '/admin/onboarding') {
      setNeedsOnboarding(false);
      setOnboardingChecked(false);
    }
  }, [location]);

  // Check onboarding status for admin users on admin routes
  useEffect(() => {
    const checkOnboarding = async () => {
      // Only check for admin users on admin routes (except onboarding page itself).
      // Skip while impersonating — a super-admin should land on the target's
      // dashboard, not be bounced into that tenant's onboarding wizard.
      if (
        user?.role === 'admin' &&
        !impersonation &&
        location.startsWith('/admin') &&
        location !== '/admin/onboarding' &&
        !onboardingChecked
      ) {
        setCheckingOnboarding(true);
        try {
          const { onboarding } = await organizationService.getOnboardingStatus();
          if (!onboarding.completed && !onboarding.skipped) {
            setNeedsOnboarding(true);
          } else {
            setNeedsOnboarding(false);
          }
        } catch (error) {
          console.error('Failed to check onboarding status:', error);
          // Continue without redirect if check fails
        }
        setCheckingOnboarding(false);
        setOnboardingChecked(true);
      }
    };

    if (isAuthenticated && !loading) {
      checkOnboarding();
    }
  }, [user, location, isAuthenticated, loading, onboardingChecked, impersonation]);

  if (loading || checkingOnboarding) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  // A token in hand but no user yet means the session is still hydrating, not
  // that nobody is signed in. The Google exchange stores the token and sets the
  // user in the same tick, and wouter re-renders this subtree in between — the
  // AuthProvider lives outside <Router>, so its state update lands one render
  // later. Redirecting on that gap dropped a fresh signup on /login instead of
  // /admin/onboarding. Wait it out; a genuinely missing token still redirects.
  if (!isAuthenticated) {
    if (Cookies.get('access_token') && !hydrationTimedOut) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      );
    }

    return <Redirect to={redirectTo} />;
  }

  // Redirect to onboarding if needed
  if (needsOnboarding && location !== '/admin/onboarding') {
    return <Redirect to="/admin/onboarding" />;
  }

  if (requiredRole && user?.role !== requiredRole) {
    // Check if user has sufficient privileges
    const roleHierarchy = { customer: 0, staff: 1, admin: 2 };
    const userLevel = roleHierarchy[user?.role || 'customer'];
    const requiredLevel = roleHierarchy[requiredRole];

    if (userLevel < requiredLevel) {
      return <Redirect to="/unauthorized" />;
    }
  }

  return <>{children}</>;
}
